@let status = statusSg();
@let isPending = status === 'pending';
@let steps = displayStepsSg();
@let stepCount = steps.length;
@let expanded = expandedSg();
@let triggerLabel =
  expanded
    ? 'Hide reasoning'
    : stepCount + ' reasoning step' + (stepCount === 1 ? '' : 's');
@let currentStep = currentStepLabelSg();
@let payloadTabs = payloadTabsSg();

@if (stepCount > 0) {
  <div class="atn-message-info">
    <button
      class="atn-message-info__trigger"
      [class.atn-message-info__trigger--pending]="isPending"
      type="button"
      [attr.aria-expanded]="expanded"
      (click)="toggleExpanded()"
    >
      <i class="atn-icon atn-comment atn-blue"></i>
      <span class="atn-message-info__text">{{ triggerLabel }}</span>
      @if (!expanded && isPending && currentStep) {
        <span
          class="atn-message-info__current"
          aria-live="polite"
        >
          <span
            class="atn-message-info__current-spinner"
            aria-hidden="true"
          ></span>
          <span class="atn-message-info__current-label">{{ currentStep }}</span>
        </span>
      }
      <i
        class="atn-icon atn-angle-down atn-blue atn-message-info__trigger-chevron"
        [class.atn-message-info__trigger-chevron--expanded]="expanded"
      ></i>
    </button>

    <div
      #panel
      class="atn-message-info__panel"
      [class.atn-message-info__panel--expanded]="expanded"
      [attr.aria-hidden]="!expanded"
      [attr.inert]="expanded ? null : ''"
    >
      <div class="atn-message-info__panel-content">
        <div class="atn-message-info__panel-title">Reasoning</div>
        <div class="atn-message-info__timeline">
          @for (step of steps; track $index) {
            <div
              class="atn-message-info__timeline-step atn-message-info__timeline-step--{{
                step.kind
              }}"
              [class.atn-message-info__timeline-step--latest]="
                $index === stepCount - 1
              "
              [class.atn-message-info__timeline-step--pending]="
                $index === stepCount - 1 && isPending
              "
              [class.atn-message-info__timeline-step--active]="
                (step.kind === 'status' && step.state === 'active') ||
                (step.kind === 'tool' && step.state === 'invoking')
              "
              [class.atn-message-info__timeline-step--failed]="
                step.kind === 'tool' && step.state === 'failed'
              "
            >
              <div class="atn-message-info__timeline-marker"></div>
              <div class="atn-message-info__timeline-content">
                @switch (step.kind) {
                  @case ('rationale') {
                    <atn-markdown [data]="step.text" />
                  }
                  @case ('text') {
                    <atn-markdown [data]="step.text" />
                  }
                  @case ('status') {
                    <div
                      class="atn-message-info__status atn-message-info__status--{{
                        step.state
                      }}"
                    >
                      @if (step.state === 'active') {
                        <span
                          class="atn-message-info__spinner"
                          aria-hidden="true"
                        ></span>
                      } @else {
                        <span
                          class="atn-message-info__check-mark"
                          aria-hidden="true"
                        ></span>
                      }
                      <span class="atn-message-info__status-label">{{
                        step.label
                      }}</span>
                    </div>
                  }
                  @case ('tool') {
                    @let payloadTab = payloadTabs.get($index);
                    <div
                      class="atn-message-info__tool atn-message-info__tool--{{
                        step.state
                      }}"
                    >
                      <div class="atn-message-info__tool-header">
                        <i
                          class="atn-icon atn-run atn-blue atn-message-info__tool-icon"
                        ></i>
                        <span class="atn-message-info__tool-name">{{
                          step.headerLabel
                        }}</span>
                        @if (step.headerOperation) {
                          <span class="atn-message-info__tool-meta">{{
                            step.headerOperation
                          }}</span>
                        }
                        @if (step.retry) {
                          <span
                            class="atn-message-info__tool-retry"
                            [class.atn-message-info__tool-retry--warn]="
                              step.retry.attempt >= step.retry.max
                            "
                            [attr.title]="
                              'Attempt ' +
                              step.retry.attempt +
                              ' of ' +
                              step.retry.max
                            "
                            >{{ step.retry.attempt }}/{{ step.retry.max }}</span
                          >
                        }
                        <span
                          class="atn-message-info__tool-badge atn-message-info__tool-badge--{{
                            step.state
                          }}"
                        >
                          @switch (step.state) {
                            @case ('invoking') {
                              <span
                                class="atn-message-info__spinner atn-message-info__spinner--inline"
                                aria-hidden="true"
                              ></span>
                              <span>Executing</span>
                            }
                            @case ('completed') {
                              <span
                                class="atn-message-info__check-mark atn-message-info__check-mark--success"
                                aria-hidden="true"
                              ></span>
                              <span>Completed</span>
                            }
                            @case ('failed') {
                              <span
                                class="atn-message-info__cross-mark"
                                aria-hidden="true"
                              ></span>
                              <span>Failed</span>
                            }
                          }
                        </span>
                      </div>

                      @if (step.state !== 'invoking' && step.error) {
                        <div class="atn-message-info__tool-error-inline">
                          {{ step.error }}
                        </div>
                      }

                      @if (step.inputText || step.outputText) {
                        <div class="atn-message-info__tool-tabs">
                          @if (step.inputText) {
                            <button
                              type="button"
                              class="atn-message-info__tool-tab"
                              [class.atn-message-info__tool-tab--active]="
                                payloadTab === 'request'
                              "
                              [attr.aria-expanded]="payloadTab === 'request'"
                              (click)="togglePayloadTab($index, 'request')"
                            >
                              <span>{{ step.inputLabel ?? 'Request' }}</span>
                            </button>
                          }
                          @if (step.outputText) {
                            <button
                              type="button"
                              class="atn-message-info__tool-tab atn-message-info__tool-tab--response"
                              [class.atn-message-info__tool-tab--active]="
                                payloadTab === 'response'
                              "
                              [class.atn-message-info__tool-tab--failed]="
                                step.state === 'failed'
                              "
                              [attr.aria-expanded]="payloadTab === 'response'"
                              (click)="togglePayloadTab($index, 'response')"
                            >
                              <span>{{ step.outputLabel ?? 'Response' }}</span>
                            </button>
                          }
                        </div>

                        @if (step.inputText && payloadTab === 'request') {
                          @if (step.inputData !== undefined) {
                            <div class="atn-message-info__tool-payload-tree">
                              <atn-json-tree [data]="step.inputData" />
                            </div>
                          } @else {
                            <pre class="atn-message-info__tool-payload">{{
                              step.inputText
                            }}</pre>
                          }
                        }
                        @if (step.outputText && payloadTab === 'response') {
                          @if (step.outputData !== undefined) {
                            <div class="atn-message-info__tool-payload-tree">
                              <atn-json-tree [data]="step.outputData" />
                            </div>
                          } @else {
                            <pre class="atn-message-info__tool-payload">{{
                              step.outputText
                            }}</pre>
                          }
                        }
                      }
                    </div>
                  }
                  @case ('file') {
                    <div class="atn-message-info__file">
                      <i class="atn-icon atn-file"></i>
                      <span>{{ step.name }}</span>
                    </div>
                  }
                  @case ('error') {
                    <div class="atn-message-info__error">
                      <i class="atn-icon atn-warning"></i>
                      <span>{{ step.message }}</span>
                    </div>
                  }
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
